import React, { useLayoutEffect, useRef, useState } from 'react';
import { Colors, Icon } from '@cognite/cogs.js';
import styled, { css } from 'styled-components/macro';
import { ConsistencyCollectionView } from '../types';
import { CollectionCard } from './CollectionCard';

type CollectionCardsProps = {
  collections: ConsistencyCollectionView[];
  selectedCollectionId?: string;
  onSelectCollection: (id: string) => void;
  onCreateCollection: VoidFunction;
  onEditCollection: (id: string) => void;
  onDeleteCollection: (id: string) => void;
};

export const CollectionCards = ({
  collections,
  selectedCollectionId,
  onSelectCollection,
  onCreateCollection,
  onEditCollection,
  onDeleteCollection,
}: CollectionCardsProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const checkScrollable = () => {
      setIsScrollable(element.scrollWidth > element.clientWidth);
    };

    const observer = new ResizeObserver(checkScrollable);
    observer.observe(element);

    checkScrollable();

    return () => observer.disconnect();
  }, [collections]);

  return (
    <StyledContainer>
      <StyledCardsWrapper ref={ref} isScrollable={isScrollable}>
        <StyledCollectionCards>
          {collections.map((collection) => (
            <CollectionCard
              key={`card-${collection.externalId}`}
              collection={collection}
              isActive={collection.externalId === selectedCollectionId}
              onSelect={onSelectCollection}
              onEdit={onEditCollection}
              onDelete={onDeleteCollection}
            />
          ))}
          <StyledAddButton
            aria-label="Create collection"
            onClick={onCreateCollection}
          >
            <Icon type="AddLarge" />
          </StyledAddButton>
        </StyledCollectionCards>
      </StyledCardsWrapper>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledCardsWrapper = styled.div<{ isScrollable: boolean }>`
  overflow-x: auto;
  overflow-y: hidden;
  ${({ isScrollable }) =>
    isScrollable &&
    css`
      &::-webkit-scrollbar {
        height: 6px;
      }
      &::-webkit-scrollbar-track {
        background: var(--cogs-bg-accent);
      }
      &::-webkit-scrollbar-thumb {
        background: var(--cogs-greyscale-grey5);
        border-radius: 3px;
      }
    `}
`;

const StyledCollectionCards = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0 2rem 0.5rem;
  min-width: min-content;
`;

const StyledAddButton = styled.button`
  min-width: 180px;
  max-width: 200px;
  align-self: stretch;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 2px dashed var(--cogs-bg-control--disabled);
  background: ${Colors['decorative--grayscale--white']};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--cogs-primary);
    background: var(--cogs-primary--light);
  }

  .cogs-icon {
    font-size: 24px;
  }
`;
